import { useCallback, useEffect, useRef, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import type { DeviceProfile } from "../types";

type UseDeviceProfileParams = {
  deviceId: string | null;
  historyPage: number;
  historyPageSize: number;
};

type UseDeviceProfileResult = {
  profile: DeviceProfile | null;
  isLoading: boolean;
  errorMessage: string;
  refresh: () => void;
};

export const useDeviceProfile = ({
  deviceId,
  historyPage,
  historyPageSize,
}: UseDeviceProfileParams): UseDeviceProfileResult => {
  const { performRequest } = useApp();
  const [profile, setProfile] = useState<DeviceProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(() => {
    setRefreshTick((tick) => tick + 1);
  }, []);

  useEffect(() => {
    if (!deviceId) {
      setProfile(null);
      setErrorMessage("");
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    const query = new URLSearchParams({
      historyPage: String(historyPage),
      historyPageSize: String(historyPageSize),
    });

    setIsLoading(true);
    setErrorMessage("");

    performRequest<DeviceProfile | null>(
      `/devices/${encodeURIComponent(deviceId)}?${query.toString()}`,
      { signal: controller.signal },
    )
      .then((response) => {
        if (controller.signal.aborted) return;
        setProfile(response ?? null);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;

        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }

        setProfile(null);
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Failed to load device profile",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [performRequest, deviceId, historyPage, historyPageSize, refreshTick]);

  return { profile, isLoading, errorMessage, refresh };
};
